<script>
import { authState } from "@app/store/authStore.svelte.js";
import { modalState, hideModal, showModal } from "@app/store/modalStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { fade } from "svelte/transition";
import { BarLoader } from "svelte-loading-spinners";
import * as m from "@app/paraglide/messages.js";
import { auth } from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";
import { collection, doc, getDocs, setDoc, deleteDoc, query, where, Timestamp } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";

const SHARE_BASE_URL = "https://app.closedcaptioncreator.com/share?id=";

let view = $state("list");
let shareCodes = $state([]);
let createdShare = $state(null);
let hasCommits = $state(true);
let loading = $state(false);
let saving = $state(false);
let loadError = $state("");
let isNewShare = $state(false);
let memberQuery = $state("");
let groupQuery = $state("");
let accessMode = $state("specific");
let originalMembers = $state([]);
let form = $state(emptyForm());
let showErrors = $state(false);

const hasTeam = $derived((authState.team?.members?.length ?? 0) > 1);
const teamMemberEmails = $derived(
	(authState.team?.members || []).map(normalizeEmail).filter(Boolean)
);
const liveEventGroups = $derived(
	eventGroupState.filter((group) => group && group.state !== "deleted")
);
const filteredMembers = $derived.by(() => {
	const q = memberQuery.trim().toLowerCase();
	if (!q) {
		return teamMemberEmails;
	}
	return teamMemberEmails.filter((email) => email.includes(q));
});
const filteredGroups = $derived.by(() => {
	const q = groupQuery.trim().toLowerCase();
	if (!q) {
		return liveEventGroups;
	}
	return liveEventGroups.filter((group) => {
		const name = String(group.name || "").toLowerCase();
		const type = String(group.type || "").toLowerCase();
		const language = String(group.language || "").toLowerCase();
		return name.includes(q) || type.includes(q) || language.includes(q);
	});
});
const liveGroupIds = $derived(new Set(liveEventGroups.map((group) => group.id)));
const unavailableGroups = $derived(
	(form.groups || []).filter((id) => id && !liveGroupIds.has(id))
);
const nameValid = $derived(Boolean(form.name?.trim()));
const membersValid = $derived((form.members?.length ?? 0) > 0);
const groupsValid = $derived(accessMode === "full" || (form.groups?.length ?? 0) > 0);
const formValid = $derived(nameValid && membersValid && groupsValid);
const showNameError = $derived(showErrors && !nameValid);
const showMembersError = $derived(showErrors && !membersValid);
const showGroupsError = $derived(showErrors && !groupsValid);
const removedMembers = $derived.by(() => {
	if (isNewShare) {
		return [];
	}
	const current = new Set((form.members || []).map(normalizeEmail));
	return originalMembers.filter((email) => !current.has(normalizeEmail(email)));
});

function emptyForm() {
	return {
		id: "",
		name: "",
		members: [],
		groups: [],
		access: "editor",
		fullPermissions: false,
		ownerId: "",
		ownerEmail: "",
		teamId: "",
		projectTeamId: "",
		createdOn: null,
	};
}

function resetForm() {
	form = emptyForm();
	isNewShare = false;
	accessMode = "specific";
	originalMembers = [];
	memberQuery = "";
	groupQuery = "";
	showErrors = false;
}

function normalizeEmail(email) {
	return String(email || "").trim().toLowerCase();
}

function getSharesRef() {
	const teamId = authState.team?.id;
	if (!teamId) {
		return null;
	}
	return collection(db, "teams", teamId, "shares");
}

function getSnapshotsRef() {
	const teamId = authState.team?.id;
	const projectTeamId = projectState.teamId;
	if (!teamId || !projectTeamId) {
		return null;
	}
	return collection(db, "teams", teamId, "projects", projectTeamId, "snapshots");
}

function shareUrl(id) {
	return `${SHARE_BASE_URL}${id}`;
}

function createdOnMillis(value) {
	if (!value) {
		return 0;
	}
	if (typeof value.toMillis === "function") {
		return value.toMillis();
	}
	if (typeof value.toDate === "function") {
		return value.toDate().getTime();
	}
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function formatCreatedOn(value) {
	const millis = createdOnMillis(value);
	if (!millis) {
		return "";
	}
	return new Date(millis).toLocaleString();
}

function liveEventCount(group) {
	const events = Array.isArray(group?.events) ? group.events : [];
	return events.filter((event) => event?.state !== "deleted").length;
}

function groupMeta(group) {
	return [group?.type, group?.language].filter(Boolean).join(" · ");
}

function accessBadge(share) {
	if (share?.fullPermissions) {
		return m.team_share_access_entire();
	}
	const selectedLive = (share?.groups || []).filter((id) => liveGroupIds.has(id)).length;
	return m.team_share_access_partial({
		selected: selectedLive,
		total: liveEventGroups.length,
	});
}

function memberPreview(share) {
	const members = (share?.members || []).map(normalizeEmail).filter(Boolean);
	return {
		shown: members.slice(0, 3),
		extra: Math.max(0, members.length - 3),
	};
}

function copyText(value, successMessage) {
	if (!value) {
		toast.push(m.team_share_no_share_selected(), { classes: ["toast-danger"] });
		return;
	}
	navigator.clipboard.writeText(value);
	toast.push(successMessage, { classes: ["toast-success"] });
}

function copyShareLink(id) {
	copyText(shareUrl(id), m.team_share_link_copied());
}

function copyShareCode(id) {
	copyText(id, m.team_share_code_copied());
}

function startCreate() {
	const user = auth.currentUser;
	if (!user?.uid || !authState.team?.id || !projectState.teamId) {
		toast.push(m.team_share_save_error({ message: m.team_share_create_team_prompt() }), {
			classes: ["toast-danger"],
		});
		return;
	}
	form = {
		id: uuidv4(),
		name: "",
		members: [],
		groups: [],
		access: "editor",
		fullPermissions: false,
		ownerId: user.uid,
		ownerEmail: user.email || "",
		teamId: authState.team.id,
		projectTeamId: projectState.teamId,
		createdOn: Timestamp.fromDate(new Date()),
	};
	originalMembers = [];
	isNewShare = true;
	accessMode = "specific";
	memberQuery = "";
	groupQuery = "";
	createdShare = null;
	showErrors = false;
	view = "edit";
}

function startEdit(share) {
	const snap = $state.snapshot(share);
	const members = [...new Set((snap.members || []).map(normalizeEmail).filter(Boolean))];
	form = {
		id: snap.id,
		name: snap.name || "",
		members,
		groups: [...(snap.groups || [])],
		access: snap.access || "editor",
		fullPermissions: Boolean(snap.fullPermissions),
		ownerId: snap.ownerId || auth.currentUser?.uid || "",
		ownerEmail: snap.ownerEmail || auth.currentUser?.email || "",
		teamId: snap.teamId || authState.team?.id || "",
		projectTeamId: snap.projectTeamId || projectState.teamId || "",
		createdOn: snap.createdOn || null,
	};
	originalMembers = [...members];
	isNewShare = false;
	accessMode = snap.fullPermissions ? "full" : "specific";
	memberQuery = "";
	groupQuery = "";
	showErrors = false;
	view = "edit";
}

function cancelEdit() {
	resetForm();
	view = "list";
}

function finishCreated() {
	createdShare = null;
	resetForm();
	view = "list";
}

function selectAllMembers() {
	form.members = [...teamMemberEmails];
}

function clearMembers() {
	const hadMembers = form.members.length > 0;
	form.members = [];
	if (hadMembers) {
		showErrors = true;
	}
}

function removeMember(email) {
	form.members = form.members.filter((member) => member !== email);
}

function selectAllGroups() {
	form.groups = liveEventGroups.map((group) => group.id);
}

function clearGroups() {
	const hadGroups = form.groups.length > 0;
	form.groups = [...unavailableGroups];
	if (hadGroups && form.groups.length === 0) {
		showErrors = true;
	}
}

function removeUnavailableGroup(id) {
	form.groups = form.groups.filter((groupId) => groupId !== id);
}

function onNameBlur() {
	if (!nameValid) {
		showErrors = true;
	}
}

async function setup() {
	loading = true;
	loadError = "";
	try {
		const sharesRef = getSharesRef();
		const snapshotsRef = getSnapshotsRef();
		if (!sharesRef || !projectState.teamId) {
			shareCodes = [];
			return;
		}
		const querySnapshot = await getDocs(
			query(sharesRef, where("projectTeamId", "==", projectState.teamId))
		);
		const loaded = [];
		querySnapshot.forEach((shareDoc) => {
			loaded.push(shareDoc.data());
		});
		loaded.sort((a, b) => createdOnMillis(b.createdOn) - createdOnMillis(a.createdOn));
		shareCodes = loaded;
		if (snapshotsRef) {
			const snapshotRes = await getDocs(snapshotsRef);
			hasCommits = !snapshotRes.empty;
		}
	} catch (err) {
		console.log(err, err.message);
		loadError = err.message;
		toast.push(m.team_share_get_error({ message: err.message }), { classes: ["toast-danger"] });
	} finally {
		loading = false;
	}
}

async function saveShare() {
	if (saving) {
		return;
	}
	if (!formValid) {
		showErrors = true;
		return;
	}
	const sharesRef = getSharesRef();
	const user = auth.currentUser;
	if (!sharesRef || !user?.uid || !projectState.teamId) {
		toast.push(m.team_share_save_error({ message: m.team_share_create_team_prompt() }), {
			classes: ["toast-danger"],
		});
		return;
	}

	const members = [...new Set((form.members || []).map(normalizeEmail).filter(Boolean))];
	const groups =
		accessMode === "full"
			? liveEventGroups.map((group) => group.id)
			: [...(form.groups || [])];

	const payload = {
		id: form.id,
		name: form.name.trim(),
		members,
		groups,
		access: form.access || "editor",
		fullPermissions: accessMode === "full",
		ownerId: form.ownerId || user.uid,
		ownerEmail: form.ownerEmail || user.email || "",
		teamId: form.teamId || authState.team.id,
		projectTeamId: form.projectTeamId || projectState.teamId,
		createdOn: form.createdOn || Timestamp.fromDate(new Date()),
		updatedOn: Timestamp.fromDate(new Date()),
	};

	saving = true;
	try {
		await setDoc(doc(sharesRef, payload.id), payload);
		toast.push(m.team_share_saved_successfully(), { classes: ["toast-success"] });
		await setup();
		if (isNewShare) {
			createdShare = payload;
			view = "created";
		} else {
			view = "list";
		}
		resetForm();
	} catch (err) {
		console.log(err, err.message);
		toast.push(m.team_share_save_error({ message: err.message }), { classes: ["toast-danger"] });
	} finally {
		saving = false;
	}
}

async function removeShare(share) {
	if (!share?.id) {
		return;
	}
	const result = await Swal.fire({
		...SWAL_BASE,
		customClass: SWAL_CLASSES.destructive,
		title: m.team_share_delete_confirm_title(),
		text: m.team_share_delete_confirm_text(),
		icon: "warning",
		showCancelButton: true,
		confirmButtonText: m.team_share_delete_share_button(),
		cancelButtonText: m.team_share_cancel_button(),
		allowOutsideClick: false,
	});
	if (!result.isConfirmed) {
		return;
	}
	const sharesRef = getSharesRef();
	if (!sharesRef) {
		return;
	}
	try {
		await deleteDoc(doc(sharesRef, share.id));
		toast.push(m.team_share_removed_successfully(), { classes: ["toast-success"] });
		await setup();
	} catch (err) {
		console.log(err, err.message);
		toast.push(m.team_share_remove_error({ message: err.message }), { classes: ["toast-danger"] });
	}
}

if ((authState.team?.members?.length ?? 0) > 1) {
	void setup();
}
</script>

<div
	transition:fade={{ duration: 100 }}
	class={["modal", modalState.selected === "teamShare" && "show d-block"]}
	role="dialog"
	aria-labelledby="teamShareTitle"
	aria-modal="true"
	tabindex="-1"
	id="TeamShare"
>
	<div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
		<div class="modal-content">
			<div class="modal-header">
				<h4 class="modal-title" id="teamShareTitle">
					{#if view === "edit"}
						{isNewShare ? m.team_share_create_title() : m.team_share_edit_title()}
					{:else if view === "created"}
						{m.team_share_created_title()}
					{:else}
						{m.team_share_title()}
					{/if}
				</h4>
				<button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal}></button>
			</div>

			<div class="modal-body">
				{#if !hasTeam}
					<div class="text-center py-4 px-3">
						<i class="bi bi-people display-6 d-block mb-2"></i>
						<p class="mb-3">{m.team_share_create_team_prompt()}</p>
						<button type="button" class="btn btn-primary" onclick={() => showModal("teamManager")}>
							{m.team_share_manage_team_button()}
						</button>
					</div>
				{:else if view === "created" && createdShare}
					<p class="text-success fw-semibold mb-3">
						<i class="bi bi-check-circle-fill me-1"></i>
						{m.team_share_created_title()}
					</p>
					<label class="form-label" for="createdShareLink">{m.team_share_link_label()}</label>
					<div class="input-group mb-3">
						<input
							id="createdShareLink"
							type="text"
							class="form-control"
							readonly
							value={shareUrl(createdShare.id)}
						/>
						<button
							class="btn btn-primary"
							type="button"
							onclick={() => copyShareLink(createdShare.id)}
						>
							<i class="bi bi-link-45deg"></i>
							{m.team_share_copy_link_button()}
						</button>
					</div>
					<label class="form-label" for="createdShareCode">{m.team_share_code_label()}</label>
					<div class="input-group mb-4">
						<input
							id="createdShareCode"
							type="text"
							class="form-control"
							readonly
							value={createdShare.id}
						/>
						<button
							class="btn btn-outline-secondary"
							type="button"
							onclick={() => copyShareCode(createdShare.id)}
						>
							{m.team_share_copy_code_button()}
						</button>
					</div>
					<ol class="mb-0">
						<li>{m.team_share_created_step_1()}</li>
						<li>{m.team_share_created_step_2()}</li>
						<li>{m.team_share_created_step_3()}</li>
					</ol>
				{:else if view === "edit"}
					<form class="share-edit-form" onsubmit={(event) => { event.preventDefault(); void saveShare(); }}>
						<section class="mb-4">
							<div class="d-flex align-items-center gap-2 mb-2">
								<span class="badge rounded-pill bg-primary">1</span>
								<h5 class="mb-0">{m.team_share_section_name()}</h5>
							</div>
							<label class="form-label" for="shareName">{m.team_share_name_label()}</label>
							<input
								id="shareName"
								class={["form-control", showNameError && "is-invalid"]}
								type="text"
								placeholder={m.team_share_name_placeholder_example()}
								bind:value={form.name}
								onblur={onNameBlur}
							/>
							{#if showNameError}
								<div class="invalid-feedback">{m.team_share_error_name_required()}</div>
							{/if}
						</section>

						<section class="mb-4">
							<div class="d-flex align-items-center justify-content-between gap-2 mb-2">
								<div class="d-flex align-items-center gap-2">
									<span class="badge rounded-pill bg-primary">2</span>
									<h5 class="mb-0">{m.team_share_section_people()}</h5>
								</div>
							</div>
							{#if form.members.length > 0}
							<div class="d-flex flex-wrap gap-1 mb-2">
								{#each form.members as email (email)}
									<span class="badge rounded-pill text-bg-secondary d-inline-flex align-items-center gap-1">
										{email}
										<button
											type="button"
											class="btn-close btn-close-white btn-close-sm"
											aria-label={m.team_share_remove_member_aria({ email })}
											onclick={() => removeMember(email)}
										></button>
									</span>
								{/each}
							</div>
							{/if}
							<div class="share-picker border rounded">
								<div class="share-picker-toolbar">
									<span class="text-muted small">{m.team_share_selected_count({ count: form.members.length })}</span>
									<div class="d-flex gap-2">
										<button class="btn btn-outline-secondary btn-sm" type="button" onclick={selectAllMembers}>
											{m.team_share_select_all_button()}
										</button>
										<button class="btn btn-outline-secondary btn-sm" type="button" onclick={clearMembers}>
											{m.team_share_clear_button()}
										</button>
									</div>
								</div>
								<input
									class="form-control border-0 rounded-0"
									type="search"
									placeholder={m.team_share_search_members_placeholder()}
									bind:value={memberQuery}
									aria-label={m.team_share_search_members_placeholder()}
								/>
								<div class="share-checklist">
									{#each filteredMembers as member (member)}
										<label class="share-checklist-item">
											<input class="form-check-input" type="checkbox" bind:group={form.members} value={member} />
											<span>{member}</span>
										</label>
									{/each}
								</div>
							</div>
							{#if showMembersError}
								<div class="invalid-feedback d-block">{m.team_share_error_members_required()}</div>
							{/if}
							{#if removedMembers.length > 0}
								<p class="text-muted small mt-2 mb-0">{m.team_share_removed_members_note()}</p>
							{/if}
						</section>

						<section>
							<div class="d-flex align-items-center gap-2 mb-2">
								<span class="badge rounded-pill bg-primary">3</span>
								<h5 class="mb-0" id="shareAccessHeading">{m.team_share_section_access()}</h5>
							</div>
							<div role="radiogroup" aria-labelledby="shareAccessHeading">
							<label
								class={[
									"share-access-card border rounded p-3 mb-2",
									accessMode === "full" && "border-primary",
								]}
							>
								<input class="form-check-input" type="radio" name="shareAccess" bind:group={accessMode} value="full" />
								<span>
									<span class="fw-semibold">{m.team_share_access_entire()}</span>
									<p class="text-muted small mb-0">{m.team_share_access_entire_desc()}</p>
								</span>
							</label>
							<label
								class={[
									"share-access-card border rounded p-3 mb-2",
									accessMode === "specific" && "border-primary",
								]}
							>
								<input class="form-check-input" type="radio" name="shareAccess" bind:group={accessMode} value="specific" />
								<span>
									<span class="fw-semibold">{m.team_share_access_specific()}</span>
									<p class="text-muted small mb-0">{m.team_share_access_specific_desc()}</p>
								</span>
							</label>
							</div>

							{#if accessMode === "specific"}
								<div class="share-picker border rounded">
									<div class="share-picker-toolbar">
										<span class="text-muted small">
											{m.team_share_selected_count({ count: form.groups.length })}
										</span>
										<div class="d-flex gap-2">
											<button class="btn btn-outline-secondary btn-sm" type="button" onclick={selectAllGroups}>
												{m.team_share_select_all_button()}
											</button>
											<button class="btn btn-outline-secondary btn-sm" type="button" onclick={clearGroups}>
												{m.team_share_clear_button()}
											</button>
										</div>
									</div>
									<input
										class="form-control border-0 rounded-0"
										type="search"
										placeholder={m.team_share_search_groups_placeholder()}
										bind:value={groupQuery}
										aria-label={m.team_share_search_groups_placeholder()}
									/>
									{#if unavailableGroups.length > 0}
										<div class="px-2 pt-2">
											{#each unavailableGroups as groupId (groupId)}
												<div class="d-flex align-items-center justify-content-between border rounded px-2 py-1 mb-1">
													<span class="small text-muted">{m.team_share_unavailable_group()}</span>
													<button
														type="button"
														class="btn btn-sm btn-outline-secondary"
														aria-label={m.team_share_remove_group_aria()}
														onclick={() => removeUnavailableGroup(groupId)}
													>
														{m.team_share_clear_button()}
													</button>
												</div>
											{/each}
										</div>
									{/if}
									<div class="share-checklist share-checklist-groups">
										{#each filteredGroups as eventGroup (eventGroup.id)}
											<label class="share-checklist-item">
												<input
													class="form-check-input"
													type="checkbox"
													bind:group={form.groups}
													value={eventGroup.id}
												/>
												<span class="d-flex justify-content-between align-items-center w-100 gap-2">
													<span>
														{eventGroup.name}
														{#if groupMeta(eventGroup)}
															<span class="badge text-bg-secondary ms-1">{groupMeta(eventGroup)}</span>
														{/if}
													</span>
													<span class="text-muted small">{m.team_share_event_count({ count: liveEventCount(eventGroup) })}</span>
												</span>
											</label>
										{/each}
									</div>
								</div>
								{#if showGroupsError}
									<div class="invalid-feedback d-block">{m.team_share_error_groups_required()}</div>
								{/if}
							{/if}
						</section>
					</form>
				{:else}
					<p class="text-muted">{m.team_share_intro()}</p>
					{#if !hasCommits}
						<div class="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-3" role="alert">
							<div>
								<strong class="d-block">{m.team_share_no_commits_title()}</strong>
								<span>{m.team_share_no_commits_text()}</span>
							</div>
							<button type="button" class="btn btn-warning flex-shrink-0" onclick={() => showModal("teamSync")}>
								{m.team_share_sync_now_button()}
							</button>
						</div>
					{/if}
					{#if loading}
						<div class="d-flex justify-content-center align-items-center p-3">
							<BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
						</div>
					{:else if loadError}
						<p class="text-danger">{m.team_share_load_error()}</p>
					{:else if shareCodes.length === 0}
						<div class="text-center py-4">
							<i class="bi bi-folder-symlink display-6 d-block mb-2"></i>
							<p class="fw-semibold mb-3">{m.team_share_empty_title()}</p>
							<button type="button" class="btn btn-primary" onclick={startCreate}>
								<i class="bi bi-plus-lg"></i>
								{m.team_share_create_share_button()}
							</button>
						</div>
					{:else}
						<div class="d-flex align-items-center justify-content-between mb-3">
							<h5 class="mb-0">
								{m.team_share_shares_label()}
								<span class="badge rounded-pill bg-secondary ms-2">{m.team_share_links_count({ count: shareCodes.length })}</span>
							</h5>
							<button type="button" class="btn btn-primary btn-sm" onclick={startCreate}>
								<i class="bi bi-plus-lg"></i>
								{m.team_share_new_share_button()}
							</button>
						</div>
						{#each shareCodes as share (share.id)}
							{@const preview = memberPreview(share)}
							<div class="card mb-2">
								<div class="card-body">
									<div class="d-flex justify-content-between gap-3 flex-wrap">
										<div>
											<h5 class="card-title mb-1">{share.name}</h5>
											<span class="badge text-bg-info text-dark">{accessBadge(share)}</span>
											<div class="d-flex flex-wrap gap-1 mt-2">
												{#each preview.shown as email (email)}
													<span class="badge rounded-pill text-bg-secondary">{email}</span>
												{/each}
												{#if preview.extra > 0}
													<span class="badge rounded-pill text-bg-secondary">{m.team_share_more_members({ count: preview.extra })}</span>
												{/if}
											</div>
											{#if formatCreatedOn(share.createdOn)}
												<p class="text-muted small mb-0 mt-2">{m.team_share_created_on({ date: formatCreatedOn(share.createdOn) })}</p>
											{/if}
										</div>
										<div class="d-flex flex-column gap-2">
											<button type="button" class="btn btn-primary btn-sm" onclick={() => copyShareLink(share.id)}>
												<i class="bi bi-link-45deg"></i>
												{m.team_share_copy_link_button()}
											</button>
											<button type="button" class="btn btn-outline-secondary btn-sm" onclick={() => startEdit(share)}>
												{m.team_share_edit_button()}
											</button>
											<button type="button" class="btn btn-outline-danger btn-sm" onclick={() => removeShare(share)}>
												<i class="bi bi-trash"></i>
												{m.team_share_delete_share_button()}
											</button>
										</div>
									</div>
								</div>
							</div>
						{/each}
					{/if}
				{/if}
			</div>

			{#if hasTeam && view === "edit"}
				<div class="modal-footer">
					<button type="button" class="btn btn-outline-secondary" onclick={cancelEdit} disabled={saving}>
						{m.team_share_back_button()}
					</button>
					<button type="button" class="btn btn-primary" onclick={() => void saveShare()} disabled={!formValid || saving}>
						{m.team_share_save_button()}
					</button>
				</div>
			{:else if hasTeam && view === "created"}
				<div class="modal-footer">
					<button type="button" class="btn btn-primary" onclick={finishCreated}>
						{m.team_share_done_button()}
					</button>
				</div>
			{/if}
		</div>
	</div>
</div>

<style>
.share-edit-form {
	padding-bottom: 0.5rem;
}
.share-picker-toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	padding: 0.5rem 0.75rem;
	border-bottom: 1px solid var(--bs-border-color);
}
.share-picker :global(.form-control) {
	box-shadow: none;
}
.share-checklist {
	max-height: 8rem;
	overflow-y: auto;
	border-top: 1px solid var(--bs-border-color);
}
.share-checklist-groups {
	max-height: 10rem;
}
.share-checklist-item {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin: 0;
	padding: 0.5rem 0.75rem;
	border-bottom: 1px solid var(--bs-border-color);
}
.share-checklist-item:last-child {
	border-bottom: 0;
}
.share-checklist-item :global(.form-check-input),
.share-access-card :global(.form-check-input) {
	position: static;
	float: none;
	margin: 0;
	flex-shrink: 0;
}
.share-access-card {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	cursor: pointer;
}
.btn-close-sm {
	width: 0.5em;
	height: 0.5em;
	font-size: 0.5rem;
}
</style>
